import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Download, Wifi, WifiOff, Cloud, CheckCircle2, AlertCircle, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  savePackingListOffline, 
  getAllPackingListsOffline,
  isOfflineAvailable,
  isOnline 
} from '@/utils/offlineStorage';

interface PackingList {
  id: string;
  destination: string;
  country: string;
  tripType: string;
  categories: any[];
  syncedAt?: number;
}

export function OfflinePackingManager() {
  const [offlineLists, setOfflineLists] = useState<PackingList[]>([]);
  const [onlineLists, setOnlineLists] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { hasFeature } = useSubscription();

  useEffect(() => {
    loadOfflineLists();
    loadOnlineLists();

    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user]);

  const loadOfflineLists = async () => {
    const lists = await getAllPackingListsOffline();
    setOfflineLists(lists);
  };

  const loadOnlineLists = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('trips')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setOnlineLists(data || []);
    } catch (error) {
      console.error('Error loading trips:', error);
    }
  };

  const syncListOffline = async (trip: any) => {
    setSyncing(true);
    try {
      await savePackingListOffline(
        trip.id,
        trip.destination,
        trip.destination_country,
        trip.trip_type,
        trip.packing_list || []
      );
      toast.success(`"${trip.destination}" synced for offline access`);
      loadOfflineLists();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync packing list');
    } finally {
      setSyncing(false);
    }
  };

  const syncAllLists = async () => {
    setSyncing(true);
    try {
      for (const trip of onlineLists) {
        await savePackingListOffline(
          trip.id,
          trip.destination,
          trip.destination_country,
          trip.trip_type,
          trip.packing_list || []
        );
      }
      toast.success(`${onlineLists.length} lists synced for offline access`);
      loadOfflineLists();
    } catch (error) {
      console.error('Error syncing all:', error);
      toast.error('Failed to sync all lists');
    } finally {
      setSyncing(false);
    }
  };

  if (!isOfflineAvailable()) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Offline Mode Unavailable
          </CardTitle>
          <CardDescription>
            Your browser doesn't support offline storage.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const isLocked = !hasFeature('offline_mode');

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer transition-colors bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent hover:from-cyan-50 dark:hover:from-cyan-950/25">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                    <Cloud className="h-4 w-4" />
                  </span>
                  <span className="truncate">Offline Access</span>
                  {isLocked && <Lock className="h-4 w-4 text-muted-foreground shrink-0" />}
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground shrink-0" />
                  )}
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm pl-11">
                  Download packing lists to access them without internet
                </CardDescription>
              </div>
              <Badge className={cn(
                "gap-1 ml-2 shrink-0 border-0",
                online
                  ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-sm shadow-cyan-500/20"
                  : "bg-muted text-muted-foreground"
              )}>
                {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                {online ? "Online" : "Offline"}
              </Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {isLocked ? (
              <UpgradePrompt
                feature="offline_mode"
                requiredTier="first_class"
                title="Access Your Packing Lists Offline"
                description="Download packing lists to access them without internet"
                benefits={[
                  "Access all your packing lists without internet",
                  "Sync lists for offline use on trips",
                  "Perfect for flights and remote destinations"
                ]}
              >
                <div />
              </UpgradePrompt>
            ) : (
              <>
                {/* Offline Lists */}
                <div>
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                    <Download className="h-4 w-4" />
                    Available Offline ({offlineLists.length})
                  </h3>
                  {offlineLists.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground border-2 border-dashed rounded-lg">
                      <Download className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No lists downloaded yet</p>
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {offlineLists.map((list) => (
                        <div key={list.id} className="p-2 border rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <div>
                              <p className="font-medium text-sm">{list.destination}</p>
                              <p className="text-xs text-muted-foreground">{list.country}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">Synced</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Online Lists to Sync */}
                {online && onlineLists.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <Cloud className="h-4 w-4" />
                        Your Recent Trips
                      </h3>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={syncAllLists}
                        disabled={syncing}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Sync All
                      </Button>
                    </div>
                    <div className="grid gap-2">
                      {onlineLists.slice(0, 3).map((trip) => {
                        const isDownloaded = offlineLists.some(l => l.id === trip.id);
                        return (
                          <div key={trip.id} className="p-2 border rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Wifi className="h-4 w-4 text-primary" />
                              <p className="font-medium text-sm">{trip.destination}</p>
                            </div>
                            <Button
                              size="sm"
                              variant={isDownloaded ? "outline" : "default"}
                              onClick={() => syncListOffline(trip)}
                              disabled={syncing}
                            >
                              {isDownloaded ? "Re-sync" : "Download"}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}